import { EntryEditError, imageTypeOf } from '../edits.ts';
import { javaFixed, javaIsBlank, javaRound } from '../javaText.ts';
import { int, scalar, transaction, type SqlConnection } from '../sql.ts';
import { GLYPH_SIZE, type MapIcon } from './icons.ts';

/**
 * The Icon Library (docs/PARITY-STEP-4-PLAN.md section 10), as the desktop's `icons` package:
 * - an SVG becomes a single-colour glyph (`SvgIconImporter`): its filled shapes gathered into
 *   one path, fitted into the 24 by 24 box; one it can't draw faithfully is refused with a
 *   reason. Its markup is read here, never put into a page (decision 6);
 * - PNG, JPEG and WebP pictures are kept as they are, up to 4 MB;
 * - an icon is named from its file ("red-dragon_token.png" is "Red Dragon Token");
 * - deleting an icon sends the pins that showed it back to their category's icon, and the
 *   categories that showed it back to a plain dot.
 * Checked against `schema/fixtures/icon-import-cases.json`.
 */

export const ICON_LIMITS = { bytes: 4 * 1024 * 1024, name: 200, pack: 200, svgPath: 200_000 } as const;

/** Why an icon couldn't be used. */
export class IconImportError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'IconImportError';
  }
}

// ---- a small XML reader, enough for SVG ----

export interface XmlElement {
  readonly tag: string;
  readonly attrs: ReadonlyMap<string, string>;
  readonly children: XmlElement[];
  parent: XmlElement | null;
}

const ENTITIES: Readonly<Record<string, string>> = { amp: '&', lt: '<', gt: '>', quot: '"', apos: "'" };
const decode = (value: string) =>
  value.replace(/&(#x[0-9a-fA-F]+|#\d+|[a-zA-Z]+);/g, (whole, name: string) => {
    if (name.startsWith('#x')) return String.fromCodePoint(Math.min(parseInt(name.slice(2), 16), 0x10ffff));
    if (name.startsWith('#')) return String.fromCodePoint(Math.min(parseInt(name.slice(1), 10), 0x10ffff));
    return ENTITIES[name] ?? whole;
  });

/**
 * The elements of an XML document, leniently: declarations, doctypes (with no entity
 * expansion), comments, CDATA and text are skipped; an unclosed element ends at the end.
 */
export function parseXml(text: string): XmlElement[] {
  const roots: XmlElement[] = [];
  let open = null as XmlElement | null;
  let i = 0;
  const skipTo = (end: string) => {
    const at = text.indexOf(end, i);
    i = at < 0 ? text.length : at + end.length;
  };
  while (i < text.length) {
    const lt = text.indexOf('<', i);
    if (lt < 0) break;
    i = lt;
    if (text.startsWith('<!--', i)) skipTo('-->');
    else if (text.startsWith('<![CDATA[', i)) skipTo(']]>');
    else if (text.startsWith('<?', i)) skipTo('?>');
    else if (text.startsWith('<!', i)) {
      // A doctype, perhaps with an internal subset in brackets: skipped, never expanded.
      let depth = 0;
      for (i += 2; i < text.length; i++) {
        if (text[i] === '[') depth++;
        else if (text[i] === ']') depth--;
        else if (text[i] === '>' && depth <= 0) break;
      }
      i++;
    } else if (text[i + 1] === '/') {
      const end = text.indexOf('>', i);
      const name = text.slice(i + 2, end < 0 ? text.length : end).trim();
      i = end < 0 ? text.length : end + 1;
      // Closes the nearest open element of that name, and any left open inside it.
      for (let e: XmlElement | null = open; e; e = e.parent) {
        if (e.tag === name) {
          open = e.parent;
          break;
        }
      }
    } else {
      const m = /^<([A-Za-z_][\w:.-]*)/.exec(text.slice(i, i + 200));
      if (!m) {
        i++;
        continue;
      }
      i += m[0].length;
      const attrs = new Map<string, string>();
      const attr = /\s*([^\s=/>]+)\s*(?:=\s*("([^"]*)"|'([^']*)'|([^\s>]+)))?/y;
      let selfClosing = false;
      while (i < text.length) {
        while (i < text.length && /\s/.test(text[i]!)) i++;
        if (text[i] === '>') {
          i++;
          break;
        }
        if (text.startsWith('/>', i)) {
          i += 2;
          selfClosing = true;
          break;
        }
        attr.lastIndex = i;
        const a = attr.exec(text);
        if (!a || a[0].length === 0) {
          i++;
          continue;
        }
        if (!attrs.has(a[1]!)) attrs.set(a[1]!, decode(a[3] ?? a[4] ?? a[5] ?? ''));
        i = attr.lastIndex;
      }
      const element: XmlElement = { tag: m[1]!, attrs, children: [], parent: open };
      if (open) open.children.push(element);
      else roots.push(element);
      if (!selfClosing) open = element;
    }
  }
  return roots;
}

/** The element and everything inside it, in document order (Jsoup's `getAllElements`). */
function allElements(e: XmlElement): XmlElement[] {
  return [e, ...e.children.flatMap(allElements)];
}

const normalName = (e: XmlElement) => e.tag.toLowerCase();

// ---- path data (`SvgPath`) ----

export interface PathSegment {
  readonly command: 'M' | 'L' | 'C' | 'Q' | 'A' | 'Z';
  readonly args: readonly number[];
}

/** SVG path data as absolute segments: relative, shorthand (H V S T) and compact numbers ("1.5.5") all read. */
export function parsePath(data: string): PathSegment[] {
  const s = data;
  let i = 0;
  const out: PathSegment[] = [];
  let x = 0;
  let y = 0;
  let startX = 0;
  let startY = 0;
  let lastCx = 0;
  let lastCy = 0;
  let previous = ' ';
  let command = ' ';
  const isLetter = (c: string) => /\p{L}/u.test(c);
  const skip = () => {
    while (i < s.length && (/\s/.test(s[i]!) || s[i] === ',')) i++;
  };
  const number = (): number => {
    skip();
    const start = i;
    if (s[i] === '+' || s[i] === '-') i++;
    let dot = false;
    let digits = false;
    while (i < s.length) {
      const c = s[i]!;
      if (c >= '0' && c <= '9') {
        digits = true;
        i++;
      } else if (c === '.' && !dot) {
        dot = true;
        i++;
      } else break;
    }
    if ((s[i] === 'e' || s[i] === 'E') && digits) {
      i++;
      if (s[i] === '+' || s[i] === '-') i++;
      while (i < s.length && s[i]! >= '0' && s[i]! <= '9') i++;
    }
    if (!digits) throw new IconImportError(`Expected a number at ${start}`);
    return Number(s.slice(start, i));
  };
  const flag = (): number => {
    skip();
    if (s[i] === '0' || s[i] === '1') return Number(s[i++]);
    throw new IconImportError(`Expected an arc flag at ${i}`);
  };
  skip();
  while (i < s.length) {
    const c = s[i]!;
    if (isLetter(c)) {
      command = c;
      i++;
    } else if (command === ' ') {
      throw new IconImportError('Path data must start with a command');
    } else if (command === 'M') {
      command = 'L';
    } else if (command === 'm') {
      command = 'l';
    } else if (command === 'z' || command === 'Z') {
      // The desktop's reader loops here for ever; numbers after a close need a command.
      throw new IconImportError(`Expected a command at ${i}`);
    }
    const relative = command === command.toLowerCase() && command !== command.toUpperCase();
    const upper = command.toUpperCase();
    const ox = relative ? x : 0;
    const oy = relative ? y : 0;
    switch (upper) {
      case 'M':
        x = ox + number();
        y = oy + number();
        startX = x;
        startY = y;
        out.push({ command: 'M', args: [x, y] });
        break;
      case 'L':
        x = ox + number();
        y = oy + number();
        out.push({ command: 'L', args: [x, y] });
        break;
      case 'H':
        x = ox + number();
        out.push({ command: 'L', args: [x, y] });
        break;
      case 'V':
        y = oy + number();
        out.push({ command: 'L', args: [x, y] });
        break;
      case 'C': {
        const a = [ox + number(), oy + number(), ox + number(), oy + number(), ox + number(), oy + number()];
        out.push({ command: 'C', args: a });
        [lastCx, lastCy, x, y] = [a[2]!, a[3]!, a[4]!, a[5]!];
        break;
      }
      case 'S': {
        const follows = 'CS'.includes(previous.toUpperCase());
        const a = [follows ? 2 * x - lastCx : x, follows ? 2 * y - lastCy : y, ox + number(), oy + number(), ox + number(), oy + number()];
        out.push({ command: 'C', args: a });
        [lastCx, lastCy, x, y] = [a[2]!, a[3]!, a[4]!, a[5]!];
        break;
      }
      case 'Q': {
        const a = [ox + number(), oy + number(), ox + number(), oy + number()];
        out.push({ command: 'Q', args: a });
        [lastCx, lastCy, x, y] = [a[0]!, a[1]!, a[2]!, a[3]!];
        break;
      }
      case 'T': {
        const follows = 'QT'.includes(previous.toUpperCase());
        const qx = follows ? 2 * x - lastCx : x;
        const qy = follows ? 2 * y - lastCy : y;
        const a = [qx, qy, ox + number(), oy + number()];
        out.push({ command: 'Q', args: a });
        [lastCx, lastCy, x, y] = [qx, qy, a[2]!, a[3]!];
        break;
      }
      case 'A': {
        const rx = number();
        const ry = number();
        const rotation = number();
        const large = flag();
        const sweep = flag();
        x = ox + number();
        y = oy + number();
        out.push({ command: 'A', args: [rx, ry, rotation, large, sweep, x, y] });
        break;
      }
      case 'Z':
        out.push({ command: 'Z', args: [] });
        x = startX;
        y = startY;
        break;
      default:
        throw new IconImportError(`Unknown path command ${command}`);
    }
    previous = command;
    skip();
  }
  return out;
}

/** Scaled about the origin, then moved (`SvgPath.transformed`). */
function transformed(segments: readonly PathSegment[], scale: number, dx: number, dy: number): PathSegment[] {
  return segments.map((seg) => {
    const a = [...seg.args];
    if (seg.command === 'A') {
      a[0] = a[0]! * scale;
      a[1] = a[1]! * scale;
      a[5] = a[5]! * scale + dx;
      a[6] = a[6]! * scale + dy;
    } else {
      for (let k = 0; k + 1 < a.length; k += 2) {
        a[k] = a[k]! * scale + dx;
        a[k + 1] = a[k + 1]! * scale + dy;
      }
    }
    return { command: seg.command, args: a };
  });
}

/** `SvgPath.number`: two places at most, no trailing zeros. */
function pathNumber(value: number): string {
  const rounded = javaRound(value * 100) / 100;
  return Number.isInteger(rounded) ? String(rounded) : javaFixed(rounded, 2).replace(/0+$/, '');
}

/** Compact path data, as `SvgPath.toData` writes it. */
export function pathData(segments: readonly PathSegment[]): string {
  return segments.map((seg) => seg.command + seg.args.map((v, k) => (seg.command === 'A' && (k === 3 || k === 4) ? String(Math.trunc(v)) : pathNumber(v))).join(' ')).join('');
}

// ---- importing an SVG (`SvgIconImporter`) ----

const UNSUPPORTED = new Set(['image', 'text', 'lineargradient', 'radialgradient', 'pattern', 'filter', 'mask', 'foreignobject', 'use', 'clippath', 'style']);
const SHAPES = new Set(['path', 'rect', 'circle', 'ellipse', 'polygon', 'polyline']);

const styleValue = (style: string, property: string): string => {
  for (const declaration of style.split(';')) {
    const colon = declaration.indexOf(':');
    if (colon > 0 && declaration.slice(0, colon).trim().toLowerCase() === property.toLowerCase()) return declaration.slice(colon + 1).trim();
  }
  return '';
};

function inherited(element: XmlElement, attribute: string): string {
  for (let e: XmlElement | null = element; e; e = e.parent) {
    const value = e.attrs.get(attribute) || styleValue(e.attrs.get('style') ?? '', attribute);
    if (value) return value.trim();
  }
  return '';
}

/** `length`: "32px" is 32; anything unreadable is 0. */
function length(value: string | undefined): number {
  const v = (value ?? '').replace(/[a-zA-Z%]+$/, '').trim();
  return /^[+-]?(\d+\.?\d*|\.\d+)([eE][+-]?\d+)?$/.test(v) ? Number(v) : 0;
}

function viewBox(svg: XmlElement): [number, number, number, number] {
  const text = svg.attrs.get('viewBox') || svg.attrs.get('viewbox') || '';
  if (text.trim() !== '') {
    const parts = text.trim().split(/[\s,]+/);
    if (parts.length === 4 && parts.every((p) => /^[+-]?(\d+\.?\d*|\.\d+)([eE][+-]?\d+)?$/.test(p))) {
      const box = parts.map(Number) as [number, number, number, number];
      if (box[2] > 0 && box[3] > 0) return box;
    }
  }
  const width = length(svg.attrs.get('width'));
  const height = length(svg.attrs.get('height'));
  if (width > 0 && height > 0) return [0, 0, width, height];
  throw new IconImportError("It doesn't say how big it is (no viewBox, width, or height).");
}

// Numbers are written into the gathered path as Java's `Double.toString` would; the parser reads either.
const ellipseData = (cx: number, cy: number, rx: number, ry: number) =>
  rx <= 0 || ry <= 0 ? '' : `M${cx - rx} ${cy}A${rx} ${ry} 0 1 1 ${cx + rx} ${cy}A${rx} ${ry} 0 1 1 ${cx - rx} ${cy}Z`;

function shapeData(e: XmlElement, tag: string): string {
  const n = (name: string) => length(e.attrs.get(name));
  switch (tag) {
    case 'path':
      return e.attrs.get('d') ?? '';
    case 'rect': {
      const [x, y, w, h] = [n('x'), n('y'), n('width'), n('height')];
      return w <= 0 || h <= 0 ? '' : `M${x} ${y}H${x + w}V${y + h}H${x}Z`;
    }
    case 'circle':
      return ellipseData(n('cx'), n('cy'), n('r'), n('r'));
    case 'ellipse':
      return ellipseData(n('cx'), n('cy'), n('rx'), n('ry'));
    case 'polygon':
    case 'polyline': {
      const values = (e.attrs.get('points') ?? '').trim().split(/[\s,]+/);
      if (values.length < 6) return '';
      let d = 'M';
      for (let k = 0; k + 1 < values.length; k += 2) d += `${k === 0 ? '' : 'L'}${values[k]} ${values[k + 1]}`;
      return `${d}Z`;
    }
    default:
      return '';
  }
}

/** Whether a shape is just the whole box: a background square, not part of the icon (`coversBox`). */
function coversBox(data: string, box: readonly number[]): boolean {
  let segments: PathSegment[];
  try {
    segments = parsePath(data);
  } catch {
    return false;
  }
  // `isRectangle` first: straight lines only, so the bounds are its points'.
  const curves = segments.filter((s) => 'CQA'.includes(s.command)).length;
  const moves = segments.filter((s) => s.command === 'M').length;
  if (curves !== 0 || moves !== 1 || segments.length > 6) return false;
  const xs = segments.flatMap((s) => (s.args.length >= 2 ? [s.args[0]!] : []));
  const ys = segments.flatMap((s) => (s.args.length >= 2 ? [s.args[1]!] : []));
  if (xs.length === 0) return false;
  const [minX, maxX, minY, maxY] = [Math.min(...xs), Math.max(...xs), Math.min(...ys), Math.max(...ys)];
  const tolerance = Math.max(box[2]!, box[3]!) * 0.01;
  const fullSize = Math.abs(minX - box[0]!) <= tolerance && Math.abs(minY - box[1]!) <= tolerance && Math.abs(maxX - (box[0]! + box[2]!)) <= tolerance && Math.abs(maxY - (box[1]! + box[3]!)) <= tolerance;
  const cx = (minX + maxX) / 2;
  const cy = (minY + maxY) / 2;
  return fullSize && cx >= box[0]! && cx < box[0]! + box[2]! && cy >= box[1]! && cy < box[1]! + box[3]!;
}

export interface Glyph {
  readonly path: string;
  readonly evenOdd: boolean;
}

/** An SVG's filled shapes as one glyph in the 24 by 24 box, or `IconImportError` with why not. */
export function importSvgIcon(svgText: string): Glyph {
  const svg = parseXml(svgText)
    .flatMap(allElements)
    .find((e) => normalName(e) === 'svg');
  if (!svg) throw new IconImportError("This isn't an SVG file.");
  const box = viewBox(svg);
  let evenOdd = false;
  const parts: string[] = [];
  let outlinesOnly = 0;
  for (const element of allElements(svg)) {
    const tag = normalName(element);
    if (UNSUPPORTED.has(tag)) throw new IconImportError(`It uses <${element.tag}>, which a single-color icon can't show. Import it as a PNG picture instead.`);
    if (element !== svg && element.attrs.has('transform')) {
      throw new IconImportError('Its shapes are moved with transforms, which aren\'t supported yet. Export it as a PNG picture, or flatten it in an SVG editor first.');
    }
    if (!SHAPES.has(tag) || insideDefs(element)) continue;
    const fill = inherited(element, 'fill');
    if (fill.toLowerCase() === 'none' || fill.toLowerCase() === 'transparent') {
      outlinesOnly++;
      continue;
    }
    const data = shapeData(element, tag);
    if (javaIsBlank(data) || coversBox(data, box)) continue;
    if (inherited(element, 'fill-rule').toLowerCase() === 'evenodd' || inherited(element, 'clip-rule').toLowerCase() === 'evenodd') evenOdd = true;
    parts.push(data);
  }
  if (parts.length === 0) {
    throw new IconImportError(outlinesOnly > 0 ? 'It\'s drawn in outlines only, and icons here need filled shapes. Look for a "filled" or "solid" version of it.' : "There's nothing in it to draw.");
  }
  let combined: PathSegment[];
  try {
    combined = parsePath(parts.join(' '));
  } catch (e) {
    throw new IconImportError(`Its path data couldn't be read (${(e as Error).message}).`);
  }
  const scale = GLYPH_SIZE / Math.max(box[2], box[3]);
  const dx = -box[0] * scale + (GLYPH_SIZE - box[2] * scale) / 2;
  const dy = -box[1] * scale + (GLYPH_SIZE - box[3] * scale) / 2;
  const path = pathData(transformed(combined, scale, dx, dy));
  if (path.length > ICON_LIMITS.svgPath) throw new IconImportError('It has too much detail for an icon. Simplify it, or import it as a PNG picture.');
  return { path, evenOdd };
}

function insideDefs(e: XmlElement): boolean {
  for (let p = e.parent; p; p = p.parent) if (['defs', 'symbol'].includes(normalName(p))) return true;
  return false;
}

/** How a glyph is kept in the realm's icon table (`Glyph.encode`). */
export const encodeGlyph = (g: Glyph) => `${g.evenOdd ? 'E' : 'N'}|${g.path}`;

/** A glyph as a standalone 24 by 24 SVG, as a pack holds it (`IconPackWriter.svg`). */
export const glyphSvg = (g: Glyph) => `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path${g.evenOdd ? ' fill-rule="evenodd"' : ''} d="${g.path}"/></svg>\n`;

/** "red-dragon_token.png" is "Red Dragon Token" (`IconPackReader.displayName`). */
export function iconName(fileName: string): string {
  const base = fileName.replace(/\.[A-Za-z0-9]+$/, '').replace(/[-_]+/g, ' ').trim();
  const words = base.split(/\s+/).filter((w) => w !== '');
  return words.length === 0 ? 'Icon' : words.map((w) => w[0]!.toUpperCase() + w.slice(1)).join(' ');
}

/** A pack's file names, one each (`IconPackWriter.zip`). */
export function packFileNames(icons: readonly Pick<MapIcon, 'kind' | 'mimeType' | 'name'>[]): string[] {
  const used = new Set<string>();
  return icons.map((icon) => {
    const extension = icon.kind === 'GLYPH' ? 'svg' : icon.mimeType === 'image/jpeg' ? 'jpg' : icon.mimeType === 'image/webp' ? 'webp' : 'png';
    const base = icon.name.replace(/[^A-Za-z0-9 _-]/g, '').trim().replace(/ /g, '-').toLowerCase() || 'icon';
    let name = `${base}.${extension}`;
    for (let n = 2; used.has(name); n++) name = `${base}-${n}.${extension}`;
    used.add(name);
    return name;
  });
}

// ---- the library in the realm ----

export interface NewIcon {
  readonly pack: string;
  readonly name: string;
  readonly kind: 'GLYPH' | 'PICTURE';
  readonly mimeType: string;
  readonly data: Uint8Array;
}

/** Keeps imported icons in the realm, so they travel with it (`importIcons`); returns their ids. */
export function importIcons(conn: SqlConnection, icons: readonly NewIcon[], ids: readonly string[] = icons.map(() => crypto.randomUUID()), now: string = new Date().toISOString()): string[] {
  for (const icon of icons) checkIcon(icon);
  transaction(conn, () => {
    icons.forEach((icon, k) =>
      conn.run('INSERT INTO map_icons (id, pack, name, kind, mime_type, data, created) VALUES (?, ?, ?, ?, ?, ?, ?)', [ids[k]!, clean(icon.pack, 'Imported'), clean(icon.name, 'Icon'), icon.kind, icon.mimeType, icon.data, now]),
    );
  });
  return [...ids];
}

export function renameIcon(conn: SqlConnection, id: string, name: string): void {
  if (name.length > ICON_LIMITS.name) throw new EntryEditError('invalid', `A name can be at most ${ICON_LIMITS.name} characters.`);
  transaction(conn, () => {
    if (int(scalar(conn, 'SELECT COUNT(*) FROM map_icons WHERE id = ?', [id])) === 0) throw new EntryEditError('not_found', 'That icon no longer exists.');
    conn.run('UPDATE map_icons SET name = ? WHERE id = ?', [clean(name, 'Icon'), id]);
  });
}

/** How many pins and categories show an icon, to say before deleting it (`iconUsage`). */
export function iconUsage(conn: SqlConnection, id: string): number {
  const ref = `img:${id}`;
  return int(scalar(conn, 'SELECT (SELECT COUNT(*) FROM map_markers WHERE icon = ?) + (SELECT COUNT(*) FROM marker_categories WHERE icon = ?)', [ref, ref]));
}

/** Deletes an icon: its pins go back to their category's icon, its categories to a plain dot (`deleteIcon`). */
export function deleteIcon(conn: SqlConnection, id: string): void {
  const ref = `img:${id}`;
  transaction(conn, () => {
    if (int(scalar(conn, 'SELECT COUNT(*) FROM map_icons WHERE id = ?', [id])) === 0) throw new EntryEditError('not_found', 'That icon no longer exists.');
    conn.run("UPDATE map_markers SET icon = '' WHERE icon = ?", [ref]);
    conn.run("UPDATE marker_categories SET icon = '' WHERE icon = ?", [ref]);
    conn.run('DELETE FROM map_icons WHERE id = ?', [id]);
  });
}

function checkIcon(icon: NewIcon): void {
  if (icon.data.byteLength === 0 || icon.data.byteLength > ICON_LIMITS.bytes) throw new EntryEditError('invalid', 'An icon can be at most 4 MB.');
  if (icon.pack.length > ICON_LIMITS.pack || icon.name.length > ICON_LIMITS.name) throw new EntryEditError('invalid', 'That name is too long.');
  if (icon.kind === 'GLYPH') {
    // A glyph is kept as "E|" or "N|" and path data, nothing else.
    const text = new TextDecoder('utf-8', { fatal: false }).decode(icon.data);
    if (!/^[EN]\|[MLCQAZmlcqaz0-9 .,eE+-]*$/.test(text)) throw new EntryEditError('invalid', "That isn't an icon's shape.");
    return;
  }
  const type = imageTypeOf(icon.data);
  if (icon.kind !== 'PICTURE' || (type !== 'image/png' && type !== 'image/jpeg' && type !== 'image/webp') || type !== icon.mimeType) {
    throw new EntryEditError('invalid', "That isn't a picture Realmery can use (PNG, JPEG, WebP, or SVG).");
  }
}

const clean = (value: string, fallback: string) => (javaIsBlank(value) ? fallback : value.trim().replace(/[\r\n]+/g, ' '));
